'use client'

import React, { FC, ReactElement } from 'react'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { API_URL } from '@/utils/constants'
import axios from 'axios'
import Wallet from '@/utils/wallet'
import CollectionItem from '../collection-item'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import event from '@/utils/event'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Search, Plus, ChevronRight } from 'lucide-react'
import Fuse from 'fuse.js'
import useDebounce from '@/utils/hooks/useDebounce'
import { getSessionToken, signIn } from '@/utils/wallet/session'

const MyCollections: FC = (_props): ReactElement => {
	const [data, setData] = React.useState<any>(null)
	const [loading, setLoading] = React.useState(true)
	const [_error, setError] = React.useState('')
	const loggedIn = useLoggedIn()

	const [viewAll, setViewAll] = React.useState(false)
	const [viewAllPending, setViewAllPending] = React.useState(false)
	const [search, setSearch] = React.useState('')
	const [fuse, setFuse] = React.useState<any>(null)
	const handleChangeSearch = (evt: any) => setSearch(evt.target.value)
	const debouncedSearch: string = useDebounce<string>(search, 300)

	React.useEffect(() => {
		async function run() {
			const signature = getSessionToken()

			if (!signature) {
				setLoading(false)
				return
			}

			try {
				setLoading(true)
				const { data: d } = await axios.post(`${API_URL}/creator/collections`, {
					address: Wallet.getTaprootAddress(),
					signature
				})

				const fuseOptions = {
					isCaseSensitive: false,
					includeScore: false,
					shouldSort: true,
					includeMatches: false,
					findAllMatches: false,
					minMatchCharLength: 1,
					location: 0,
					threshold: 0.35,
					distance: 100,
					useExtendedSearch: false,
					ignoreLocation: false,
					ignoreFieldNorm: false,
					fieldNormWeight: 1,
					keys: ['name', 'slug']
				}

				const f = new Fuse(d, fuseOptions)

				setFuse(f as any)
				setData(d)
			} catch (e) {
				console.log(e)
				setError('Invalid Password')
			}

			setLoading(false)
		}
		run()
	}, [loggedIn])

	const { active, inactive } = React.useMemo(() => {
		let d = data

		if (debouncedSearch && fuse) {
			let res = (fuse as any).search(debouncedSearch)
			d = res.map((e: any) => e.item)
		}

		const active = viewAll
			? (d as any)?.filter((e: any) => e.active)
			: (d as any)?.filter((e: any) => e.active).slice(0, 8)
		const inactive = viewAllPending
			? (d as any)?.filter((e: any) => !e.active)
			: (d as any)?.filter((e: any) => !e.active).slice(0, 8)

		return { active, inactive }
	}, [data, viewAll, viewAllPending, fuse, debouncedSearch])

	const handleSubmit = async (evt: any) => {
		evt.preventDefault()
		evt.stopPropagation()

		if (!loggedIn) {
			event.emit('command', { id: 'connect-wallet' })
			return
		}

		try {
			const signature = await signIn()

			setLoading(true)
			const { data: d } = await axios.post(`${API_URL}/creator/collections`, {
				address: Wallet.getTaprootAddress(),
				signature
			})
			setData(d)
		} catch (e) {
			console.log(e)
			setError('Invalid Password')
		}

		setLoading(false)
	}

	const renderItem = (e: any) => {
		return <CollectionItem key={e.slug} data={e} />
	}

	if (loading) {
		return (
			<div className="rounded-lg border border-border bg-card p-6">
				<div className="flex items-center justify-between mb-4">
					<div className="space-y-2">
						<Skeleton className="h-5 w-36" />
						<Skeleton className="h-4 w-48" />
					</div>
					<Skeleton className="h-8 w-28 rounded-md" />
				</div>
				<Skeleton className="h-8 w-full rounded-md mb-4" />
				<div className="grid grid-cols-4 gap-2.5 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-8 2xl:grid-cols-10">
					{Array.from({ length: 10 }).map((_, i) => (
						<div key={i} className="space-y-1.5">
							<Skeleton className="aspect-square rounded-md" />
							<Skeleton className="size-3/4" />
						</div>
					))}
				</div>
			</div>
		)
	}

	if (!data) {
		return (
			<div className="rounded-lg border border-border bg-card p-6">
				<div className="space-y-1">
					<h2 className="text-base font-semibold text-foreground">My Collections</h2>
					<p className="text-sm text-muted-foreground">
						Sign a message to verify your wallet and access the creator dashboard.
					</p>
				</div>
				<Separator className="my-4" />
				<Button onClick={handleSubmit} variant="outline" className="w-full sm:w-auto">
					Authenticate
				</Button>
			</div>
		)
	}

	return (
		<div className="rounded-lg border border-border bg-card">
			<div className="flex items-center justify-between p-5 pb-0">
				<div className="space-y-0.5">
					<h2 className="text-base font-semibold text-foreground">My Collections</h2>
					<p className="text-sm text-muted-foreground">
						{(data as any).length} collection{(data as any).length !== 1 ? 's' : ''}
					</p>
				</div>
				<Link href="/creator-dashboard/new">
					<Button size="sm" className="gap-1.5">
						<Plus className="size-3.5" />
						Create
					</Button>
				</Link>
			</div>

			<div className="p-5 pb-4">
				<div className="relative">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
					<input
						placeholder="Search collections..."
						className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						onChange={handleChangeSearch}
						value={search}
					/>
				</div>
			</div>

			{inactive?.length > 0 && (
				<div className="px-5 pb-5">
					<div className="flex items-center justify-between mb-3">
						<p className="text-xs font-medium text-muted-foreground">
							Pending Approval
						</p>
						{!debouncedSearch && !viewAllPending && inactive.length >= 8 && (
							<button
								className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
								onClick={() => setViewAllPending(true)}
							>
								View all
								<ChevronRight className="size-3" />
							</button>
						)}
					</div>
					<div className="grid grid-cols-4 gap-2.5 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-8 2xl:grid-cols-10">
						{inactive.map(renderItem)}
					</div>
				</div>
			)}

			{inactive?.length > 0 && active?.length > 0 && <Separator />}

			{active?.length > 0 && (
				<div className="p-5">
					<div className="flex items-center justify-between mb-3">
						<p className="text-xs font-medium text-muted-foreground">
							Approved
						</p>
						{!debouncedSearch && !viewAll && active.length >= 8 && (
							<button
								className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
								onClick={() => setViewAll(true)}
							>
								View all
								<ChevronRight className="size-3" />
							</button>
						)}
					</div>
					<div className="grid grid-cols-4 gap-2.5 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-8 2xl:grid-cols-10">
						{active.map(renderItem)}
					</div>
				</div>
			)}

			{!inactive?.length && !active?.length && (
				<div className="px-5 pb-5">
					<div className="flex flex-col items-center justify-center py-12 text-center">
						<p className="text-sm text-muted-foreground">No collections found</p>
					</div>
				</div>
			)}
		</div>
	)
}

export default MyCollections
